import React, { useState } from 'react';
import { useInView } from './useParallax';
import { DiamondDivider, OmGlyph } from './svgs';
import './AtmaNivas.css';

const stats = [
  { value: '40', label: 'Studio Rooms', sub: '400 sq. ft. each' },
  { value: '20', label: 'Free Service Rooms', sub: '40 beds for destitute elders' },
  { value: '20', label: 'Tariff Rooms', sub: 'Self-sustaining model' },
  { value: '10 Ac', label: 'Vedanagari Ashram', sub: '2+ acres allocated for A2N' },
];

const benefits = [
  { icon: '🏠', title: 'Dedicated Room in Family Name', desc: 'A lasting legacy — your family name on a sacred room for generations.' },
  { icon: '🛏️', title: '60 Days Stay Per Year', desc: 'For self, family, or referrals. ROI through stay benefits within 8 years, then free for life.' },
  { icon: '🔗', title: '1 Referral Per Year', desc: 'End-of-life usage rights plus one annual referral for someone you care for.' },
  { icon: '🙏', title: 'Seva & Governance', desc: 'Priority access to participate in service activities and ashram governance.' },
];

const careFeatures = [
  { title: 'Integrated Nutrition', desc: 'Food aligned with health needs, served with love and Vedic tradition.' },
  { title: 'Medical Care Team', desc: '14-member dedicated team: doctors, nurses, physiotherapist, and basic life support staff.' },
  { title: 'Preventive & Responsive', desc: 'Regular check-ups, on-site nursing, emergency response, hospital tie-ups, and telemedicine.' },
  { title: 'Spiritual & Emotional', desc: 'A spiritually rooted environment ensuring emotional well-being alongside physical care.' },
];

const milestones = [
  { date: 'Apr – May 2026', event: 'Outreach & onboarding of interested participants', done: true },
  { date: 'May 28, 2026', event: 'First 20 registrations complete · Construction of first 20 rooms begins', done: false },
  { date: 'August 2026', event: 'Next 20 registrations · Construction of second 20 rooms begins', done: false },
  { date: 'Mar 15, 2027', event: 'Project completion on Shivaratri · Full-scale operations commence', done: false },
];

const faqs = [
  {
    q: 'Why is the Atmanivas project needed now?',
    a: 'There is no integrated ecosystem in Tiruvannamalai combining physical care, emotional support, and spiritual guidance for destitute elders and sadhus. Atmanivas addresses this urgent gap with a compassion-driven, holistic end-of-life care center rooted in Dharma.',
  },
  {
    q: 'Who can participate as a founding contributor?',
    a: 'Individuals, families, groups, or CSR organizations. Participation is limited to the first 40 founding contributors. Each contributor supports the cause while securing future elder care for themselves and their loved ones.',
  },
  {
    q: 'What is the Service Bank Model?',
    a: 'Sadhaks (spiritual practitioners) serve elders as part of their own spiritual practice — earning future care eligibility. This creates a self-sustaining cycle of seva: you serve today, and the community serves you tomorrow.',
  },
  {
    q: 'How will free care for destitute elders be funded?',
    a: 'The 20 tariff rooms (₹25,000/month) generate approximately ₹5 Lakhs/month, which covers operations for the 20 free service rooms. The estimated monthly cost for free care is ₹5–6 Lakhs, supported by a 14-member care team.',
  },
  {
    q: 'What amenities will the rooms have?',
    a: 'All 40 rooms are 400 sq. ft. studio units for two occupants, with modern amenities: AC, anti-skid floors, accessible toilets, electronic wheelchairs, and a full Vedic ambience. The campus has separate buildings for service and tariff areas.',
  },
  {
    q: 'What are the payment structure and timeline?',
    a: '₹10 Lakhs per participant, split as: ₹5 Lakhs at Registration (by May 2026) and ₹5 Lakhs at Completion (by November 2026). Project completion is targeted for Shivaratri, March 15, 2027.',
  },
];

function FaqItem({ faq, index }) {
  const [open, setOpen] = useState(false);
  const [ref, inView] = useInView(0.1);

  return (
    <div
      ref={ref}
      className={`a2n-faq__item ${open ? 'a2n-faq__item--open' : ''} ${inView ? 'a2n-faq__item--visible' : ''}`}
      style={{ transitionDelay: `${index * 0.06}s` }}
    >
      <button className="a2n-faq__question cinzel" onClick={() => setOpen(o => !o)}>
        <span>{faq.q}</span>
        <span className="a2n-faq__chevron">{open ? '−' : '+'}</span>
      </button>
      <div className="a2n-faq__answer">
        <p>{faq.a}</p>
      </div>
    </div>
  );
}

function StatCard({ stat, index }) {
  const [ref, inView] = useInView(0.15);
  return (
    <div
      ref={ref}
      className={`a2n-stat ${inView ? 'a2n-stat--visible' : ''}`}
      style={{ transitionDelay: `${index * 0.1}s` }}
    >
      <span className="a2n-stat__value cinzel">{stat.value}</span>
      <span className="a2n-stat__label cinzel">{stat.label}</span>
      <span className="a2n-stat__sub">{stat.sub}</span>
    </div>
  );
}

export default function AtmaNivas() {
  const [heroRef, heroInView] = useInView(0.05);
  const [needRef, needInView] = useInView(0.1);
  const [partRef, partInView] = useInView(0.1);
  const [careRef, careInView] = useInView(0.1);
  const [tlRef, tlInView] = useInView(0.1);

  return (
    <section className="a2n" id="atmanivas">

      {/* ── Hero Banner ──────────────────────────────── */}
      <div className="a2n__hero" ref={heroRef}>
        <div className="a2n__hero-bg-glow" />
        <div className={`a2n__hero-content ${heroInView ? 'a2n__hero-content--visible' : ''}`}>
          <p className="a2n__eyebrow cinzel">ॐ नमः शिवाय</p>
          <h2 className="a2n__hero-title cinzel">
            Arunachala<br />
            <span className="a2n__hero-accent">Atmanivas</span>
          </h2>
          <p className="a2n__hero-tagline cinzel">
            Dignified End-of-Life Care with Spiritual Purpose
          </p>
          <DiamondDivider color="#FF6B00" />
          <p className="a2n__hero-mantra cinzel">Seva · Dignity · Liberation</p>
          <p className="a2n__hero-sub">
            A sacred initiative to serve destitute elders, sadhus and seekers with compassion
            and dignity in their final journey — at the sacred abode of Tiruvannamalai.
          </p>
          <div className="a2n__hero-badges">
            <span className="a2n__badge">Vedanagari Ashram</span>
            <span className="a2n__badge">Tiruvannamalai</span>
            <span className="a2n__badge">Est. 2027 (Shivaratri)</span>
          </div>
        </div>
      </div>

      <div className="section-container">

        {/* ── Stats Bar ────────────────────────────────── */}
        <div className="a2n-stats">
          {stats.map((s, i) => <StatCard key={s.label} stat={s} index={i} />)}
        </div>

        {/* ── The Need & Vision ────────────────────────── */}
        <div className="a2n-block" ref={needRef}>
          <div className={`a2n-block__header ${needInView ? 'a2n-block__header--visible' : ''}`}>
            <p className="a2n__eyebrow-sm cinzel">Why Now</p>
            <h3 className="a2n-block__title cinzel">The Need &amp; <span className="a2n__accent">The Vision</span></h3>
            <DiamondDivider color="#C9902A" />
          </div>
          <div className="a2n-need__grid">
            <div className={`a2n-need__card ${needInView ? 'a2n-need__card--visible' : ''}`} style={{ transitionDelay: '0.1s' }}>
              <div className="a2n-need__icon"><OmGlyph size={40} color="#FF6B00" /></div>
              <h4 className="cinzel">The Gap</h4>
              <p>No integrated ecosystem exists in Tiruvannamalai combining <strong>physical care, emotional support,</strong> and <strong>spiritual guidance</strong> for the destitute elderly, sadhus, and seekers who arrive here to complete their final journey.</p>
            </div>
            <div className={`a2n-need__card ${needInView ? 'a2n-need__card--visible' : ''}`} style={{ transitionDelay: '0.2s' }}>
              <div className="a2n-need__icon"><OmGlyph size={40} color="#C9902A" /></div>
              <h4 className="cinzel">The Vision</h4>
              <p>A self-sustaining, spiritually rooted elder care ecosystem combining <strong>Healthcare, Nourishment, Emotional Well-being, Spiritual Guidance</strong> and <strong>Community Living</strong> — anchored in the grace of Arunachala.</p>
            </div>
            <div className={`a2n-need__card ${needInView ? 'a2n-need__card--visible' : ''}`} style={{ transitionDelay: '0.3s' }}>
              <div className="a2n-need__icon"><OmGlyph size={40} color="#F5C842" /></div>
              <h4 className="cinzel">The Location</h4>
              <p>Situated within the 10-acre <strong>Vedanagari Ashram</strong> on the Vellore Highway, 10 km from the Girivalam Path — with Arunachala hill view, Parvathamalai hill view, lush greenery, temples, gaushala, and a dining hall serving 100+ daily.</p>
            </div>
          </div>
        </div>

        {/* ── Participation Model ───────────────────────── */}
        <div className="a2n-block" ref={partRef}>
          <div className={`a2n-block__header ${partInView ? 'a2n-block__header--visible' : ''}`}>
            <p className="a2n__eyebrow-sm cinzel">Founding Contributors</p>
            <h3 className="a2n-block__title cinzel">Participation <span className="a2n__accent">Model</span></h3>
            <DiamondDivider color="#C9902A" />
          </div>

          <div className="a2n-part__layout">
            <div className={`a2n-part__contribution ${partInView ? 'a2n-part__contribution--visible' : ''}`}>
              <p className="a2n-part__limit cinzel">Limited to First <strong>40</strong> Founding Participants</p>
              <div className="a2n-part__amount">
                <span className="a2n-part__currency">₹</span>
                <span className="a2n-part__number cinzel">10 Lakhs</span>
                <span className="a2n-part__per">per participant</span>
              </div>
              <div className="a2n-part__split">
                <div className="a2n-part__tranche">
                  <span className="a2n-part__tranche-amt cinzel">₹5 Lakhs</span>
                  <span className="a2n-part__tranche-label">At Registration<br /><em>(by May 2026)</em></span>
                </div>
                <div className="a2n-part__tranche-sep">+</div>
                <div className="a2n-part__tranche">
                  <span className="a2n-part__tranche-amt cinzel">₹5 Lakhs</span>
                  <span className="a2n-part__tranche-label">At Completion<br /><em>(by Nov 2026)</em></span>
                </div>
              </div>
              <p className="a2n-part__note">35 spaces remaining · Only serious inquiries</p>
              <a href="#contact" className="a2n-part__cta cinzel" onClick={e => { e.preventDefault(); document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' }); }}>
                Express Interest
              </a>
            </div>

            <div className="a2n-part__benefits">
              <h4 className="a2n-part__benefits-title cinzel">Participant Benefits</h4>
              {benefits.map((b, i) => (
                <div
                  key={b.title}
                  className={`a2n-benefit ${partInView ? 'a2n-benefit--visible' : ''}`}
                  style={{ transitionDelay: `${0.1 + i * 0.08}s` }}
                >
                  <span className="a2n-benefit__icon">{b.icon}</span>
                  <div>
                    <p className="a2n-benefit__title cinzel">{b.title}</p>
                    <p className="a2n-benefit__desc">{b.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ── Care & Healthcare ─────────────────────────── */}
        <div className="a2n-block" ref={careRef}>
          <div className={`a2n-block__header ${careInView ? 'a2n-block__header--visible' : ''}`}>
            <p className="a2n__eyebrow-sm cinzel">Holistic Support</p>
            <h3 className="a2n-block__title cinzel">Integrated Care &amp; <span className="a2n__accent">Healthcare</span></h3>
            <DiamondDivider color="#C9902A" />
          </div>
          <div className="a2n-care__grid">
            {careFeatures.map((c, i) => (
              <div
                key={c.title}
                className={`a2n-care__card ${careInView ? 'a2n-care__card--visible' : ''}`}
                style={{ transitionDelay: `${i * 0.1}s` }}
              >
                <div className="a2n-care__bar" />
                <h4 className="a2n-care__title cinzel">{c.title}</h4>
                <p className="a2n-care__desc">{c.desc}</p>
              </div>
            ))}
          </div>
          <div className={`a2n-care__team ${careInView ? 'a2n-care__team--visible' : ''}`}>
            <p className="cinzel">Dedicated Care Team: <strong>4 Nurses · 4 Housekeeping · 2 Admin · 1 Physiotherapist · 1 Admin Staff · 1 Ambulance Driver · 1 Cook · 1 Dietician</strong></p>
            <p>Operational cost: ₹5–6 Lakhs/month · Fully covered by the self-sustaining tariff model</p>
          </div>
        </div>

        {/* ── Timeline ──────────────────────────────────── */}
        <div className="a2n-block" ref={tlRef}>
          <div className={`a2n-block__header ${tlInView ? 'a2n-block__header--visible' : ''}`}>
            <p className="a2n__eyebrow-sm cinzel">Project Journey</p>
            <h3 className="a2n-block__title cinzel">Milestones &amp; <span className="a2n__accent">Timeline</span></h3>
            <DiamondDivider color="#C9902A" />
          </div>
          <div className="a2n-timeline">
            {milestones.map((m, i) => (
              <div
                key={m.date}
                className={`a2n-timeline__item ${m.done ? 'a2n-timeline__item--done' : ''} ${tlInView ? 'a2n-timeline__item--visible' : ''}`}
                style={{ transitionDelay: `${i * 0.12}s` }}
              >
                <div className="a2n-timeline__dot" />
                <div className="a2n-timeline__content">
                  <p className="a2n-timeline__date cinzel">{m.date}</p>
                  <p className="a2n-timeline__event">{m.event}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ── FAQ ───────────────────────────────────────── */}
        <div className="a2n-block">
          <div className="a2n-block__header a2n-block__header--visible">
            <p className="a2n__eyebrow-sm cinzel">Common Questions</p>
            <h3 className="a2n-block__title cinzel">Frequently Asked <span className="a2n__accent">Questions</span></h3>
            <DiamondDivider color="#C9902A" />
          </div>
          <div className="a2n-faq">
            {faqs.map((f, i) => <FaqItem key={f.q} faq={f} index={i} />)}
          </div>
        </div>

        {/* ── CTA ───────────────────────────────────────── */}
        <div className="a2n-cta-block">
          <p className="a2n-cta-block__quote cinzel">
            "Be among the 40 founding contributors shaping a model that blends compassion, spirituality and sustainability."
          </p>
          <p className="a2n-cta-block__sub">Create Impact · Leave a Legacy · Serve Humanity</p>
          <div className="a2n-cta-block__actions">
            <a href="tel:+919611701326" className="a2n-cta-block__btn a2n-cta-block__btn--primary cinzel">
              Call +91 96117 01326
            </a>
            <a href="#contact" className="a2n-cta-block__btn a2n-cta-block__btn--outline cinzel"
              onClick={e => { e.preventDefault(); document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' }); }}>
              Contact Us
            </a>
          </div>
        </div>

      </div>
    </section>
  );
}
